import React, { useState, useEffect } from 'react'
import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { connect } from 'containers/store'

import { FieldSection } from 'components/ui/fieldset'
import { Input } from 'components/ui/input'
import { Select } from 'components/ui/select'

import { MediaField } from 'domains/media/components/media-field'

import { Media } from 'generated/mos/media'
import { VisitorInfoPage, WebPageNavigationModeValues, WebPageNavigationMode } from 'generated/mos/org/getty/visitorinfo'
import { UpdateVisitorInfoPageRequest } from 'generated/mos/org/getty/visitorinfomanagement'

import { pick, areObjectsEqual, areRefValuesEqual } from 'helpers/core'
import { convertEntityToFormValues } from 'helpers/schema'
import { Status } from 'helpers/status'

import { actionCreators, GettyActionCreators } from '../../../../actions'

import { GettyStore } from '../../../../store'

import { urlPageSchema } from '../../../../schemas/visitorInfo'

type FormValues = {
  pageUrl: string | undefined;
  navigationMode: WebPageNavigationMode;
  listTitle: string | undefined;
}

type ActionProps =
  Pick<GettyActionCreators, 'gettyUpdateVisitorInfoPageRequest'>

type ConnectedProps = Pick<GettyStore['getty'], 'visitorInfoUpdate'>

type DirectProps = {
  readonly page: VisitorInfoPage.Entity | undefined;
  readonly setSubmit: (submit: () => void) => void;
  readonly setHasSubmitted: (submitted: boolean) => void;
  readonly setModified: (modifed: boolean | undefined) => void;
}

type Props = ActionProps & ConnectedProps & DirectProps

const URLPageUpdateView = (props: Props) => {
  const {
    visitorInfoUpdate,
    gettyUpdateVisitorInfoPageRequest,
    page,
    setSubmit,
    setHasSubmitted,
    setModified,
  } = props

  // set up form values
  const [ formBag, setFormBag ] = useState<FormBag<FormValues> | undefined>(undefined)
  const [ iconMedia, setIconMedia ] = useState<Media.Ref | undefined>(undefined)

  const pageValues: Partial<VisitorInfoPage.URLWebPage.Entity & VisitorInfoPage.Entity> = pick({
    ...VisitorInfoPage.URLWebPage.defaults,
    ...VisitorInfoPage.defaults,
    ...page,
    ...(page && page.pageProperties) ? page.pageProperties: null
  }, 'pageUrl', 'navigationMode', 'listTitle')

  // setup the initial form values.
  const initialFormValues: FormValues | undefined = page ?
    convertEntityToFormValues<VisitorInfoPage.URLWebPage.Entity & VisitorInfoPage.Entity | VisitorInfoPage.URLWebPage.Entity>(pageValues, urlPageSchema) as any as FormValues
    : undefined
  const initialIconValue = page && urlPageSchema.listIconRef.read(page.listIconRef) as any as Media.Ref | undefined
  if (page && initialFormValues && !formBag && visitorInfoUpdate.status === Status.Ready) {
    setFormBag(createFormBag(initialFormValues))
    setIconMedia(initialIconValue)
  }

  useEffect(() => {
    setSubmit(() => () => onSubmit())
  })

  // handle the form ui.
  const modified = initialFormValues && formBag && (
    !areObjectsEqual(initialFormValues, formBag.values) ||
    !areRefValuesEqual(initialIconValue, iconMedia)
  )

  setModified(modified)

  const validateForm = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {};

    if (!values.listTitle || !values.listTitle.trim().length) {
      errors.listTitle = 'List title is required'
    }

    return errors
  }

  const onSubmit = () => {
    return formBag && page && submit(formBag, page)
  }

  const submit = (formBag: FormBag<FormValues>, page: VisitorInfoPage.Entity) => {
    const bag = validateFormBag(formBag, validateForm)
    setFormBag(bag)

    if(!bag.valid) {
      return false
    }

    const pageProperties = page && page.pageProperties as VisitorInfoPage.URLWebPage.Entity
    const { listTitle, pageUrl, navigationMode } = formBag.values

    setHasSubmitted(true)

    gettyUpdateVisitorInfoPageRequest({
      ...UpdateVisitorInfoPageRequest.defaults,
      visitorInfoPageRef: VisitorInfoPage.mustRef(page.ref),
      updateMask: {
        type: 'google.protobuf.FieldMask',
        fields: [
          'listIconRef',
          'listTitle',
          'urlWebPage',
        ]
      },
      listIconRef: urlPageSchema.listIconRef.write(iconMedia) || iconMedia,
      listTitle: urlPageSchema.listTitle.write(listTitle, page.listTitle) || listTitle,
      pageProperties: {
        ...VisitorInfoPage.URLWebPage.defaults,
        navigationMode: urlPageSchema.navigationMode.write(navigationMode, pageProperties.navigationMode) || navigationMode,
        pageUrl: urlPageSchema.pageUrl.write(pageUrl, pageProperties.pageUrl) || pageUrl,
      }
    })
    return false
  }

  const navigationModeLabels = {
    WEB_PAGE_NAVIGATION_MODE_MODAL: 'Opens links in a new window',
    WEB_PAGE_NAVIGATION_MODE_DISALLOWED: 'Opens links in the same window',
  }

  const navigationModeOptions = Array.from(WebPageNavigationModeValues)
    .reduce((acc: ReadonlyArray<{ label: string; value: WebPageNavigationMode }>, curr: WebPageNavigationMode ) => {
      return curr !== 'WEB_PAGE_NAVIGATION_MODE_UNSPECIFIED'
        ? [...acc, {
            label: navigationModeLabels[curr],
            value: curr
          }]
        : acc
    }, [])

  return formBag && page && (
    <form noValidate onSubmit={(event) => { event.preventDefault() }}>
      <FormData
        bag={formBag}
        onUpdate={(event) => {
          setFormBag(event.bag)
          setSubmit(() => () => onSubmit())
          setModified(modified)
        }}
        validate={() => ({})}
      >
        <Input
          label="Page URL"
          field="pageUrl"
        />
        <FieldSection />
        <Select<FormValues, 'navigationMode'>
          label="Navigation mode"
          field="navigationMode"
          options={navigationModeOptions}
          description="Open links within the same window or open a new browser window when a link is clicked"
        />

        <FieldSection />
        <Input
          label="List title"
          field="listTitle"
          error={formBag.touched.listTitle ? formBag.errors.listTitle : undefined}
          description="The list title is the page's name in the navigation menu."
        />
        <MediaField
          kind="MEDIA_KIND_RASTER_IMAGE"
          mediaRef={iconMedia}
          onUpdate={(entity) => {
            setIconMedia(entity ? entity.ref : undefined)
            setSubmit(() => () => onSubmit())
          }}
          label="List icon"
        />
      </FormData>
    </form>
  )
}

export const URLPageUpdate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  store => pick(store.getty, 'visitorInfoUpdate'),
  {
    ...pick(actionCreators, 'gettyUpdateVisitorInfoPageRequest'),
  },
)(URLPageUpdateView)
